import React from 'react';

import { AxiosError } from 'axios';

interface FallbackProps {
  error: Error | AxiosError | undefined;
}

interface ErrorBoundaryProps {
  FallbackComponent: React.ComponentType<FallbackProps>;
  setError: (err: Error | AxiosError | null) => void;
}

interface ErrorBoundaryState {
  error: Error | AxiosError | null;
}

class ErrorBoundary extends React.Component<
  React.PropsWithRef<React.PropsWithChildren<ErrorBoundaryProps>>,
  ErrorBoundaryState
> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { error: null };
  }

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  private setError = (error: Error | AxiosError) => {
    this.setState((prev) => ({ ...prev, error }));
    this.props.setError(error);
  };

  private handleError = (event: ErrorEvent) => {
    this.setError(event.error);
    event.preventDefault();
  };

  private handleRejectedPromise = async (event: PromiseRejectionEvent) => {
    event?.promise?.catch?.(this.setError);
    event.preventDefault();
  };

  componentDidMount(): void {
    window.addEventListener('error', this.handleError);
    window.addEventListener('unhandledrejection', this.handleRejectedPromise);
  }

  componentWillUnmount(): void {
    window.removeEventListener('error', this.handleError);
    window.removeEventListener('unhandledrejection', this.handleRejectedPromise);
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }

  render() {
    const { FallbackComponent, children } = this.props;
    const { error } = this.state;

    if (error) {
      const props = { error };

      return <FallbackComponent {...props} />;
    }

    return <>{children}</>;
  }
}

export { ErrorBoundary };
